import type { PrintPayloadOptions, PrintPayloadStrategy, PrintReceiptSource } from './types'
import { buildBasePayload } from './buildBasePayload'
import { buildSummarySections } from './derive/summarySections'
import { displayDiscountBillText } from './derive/labels'

/**
 * Printer interface >= 3.20 — routes `/ReceiptV2` and `/PreviewV2`, model
 * `ReceiptV5`. The renderer takes its totals from `Summary1..4` ONLY; it never
 * reads Vat / SubTotal / GrandTotal / ServiceCharge / CardCharge / Rounding.
 * A payload without the summary blocks prints items and then jumps straight to
 * Received/Change with no totals at all.
 */
export const PrintPayloadStrategyV3: PrintPayloadStrategy = {
  key: 'v3',
  usesSummarySections: true,

  build(source: PrintReceiptSource, options: PrintPayloadOptions): Record<string, any> {
    const payload = buildBasePayload(source, options)

    // Derived from `source`, before DiscountText is rewritten below: the summary
    // rows use the English checkout label ("Discount 10%"), the legacy field
    // uses the Thai one.
    //
    // `printMode` (not `isPreview`) selects the mode, because no caller has ever
    // passed 'preview' — the sections were baked as 'payment' at checkout even
    // for a preview slip, so a preview does carry the Received/Change rows.
    // Reproduced here rather than "fixed", so paper does not change.
    const sections = buildSummarySections(source, options.printMode || 'payment')
    Object.assign(payload, sections)

    payload.DiscountText = displayDiscountBillText(
      source?.DiscountRequest?.mode,
      source?.DiscountRequest?.value,
    )

    return payload
  },
}
